import { useCallback, useEffect, useState } from 'react';
import { documentService, type DocumentCategoryOption } from '@services/document.service';
import { useTenantContext } from './useTenantContext';

type CategoryStatus = 'loading' | 'ready' | 'error';
interface CategoryState {
  tenantId: string | null;
  status: CategoryStatus;
  categories: DocumentCategoryOption[];
}

/** No shared cache: a tenant change immediately hides the previous choices. */
export function useDocumentCategories() {
  const { tenantId } = useTenantContext();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<CategoryState>({
    tenantId, status: 'loading', categories: [],
  });

  useEffect(() => {
    const controller = new AbortController();
    setState({ tenantId, status: 'loading', categories: [] });
    if (!tenantId) return () => controller.abort();

    documentService.getCategories(controller.signal).then(
      (categories) => {
        if (!controller.signal.aborted) setState({ tenantId, status: 'ready', categories });
      },
      () => {
        if (!controller.signal.aborted) setState({ tenantId, status: 'error', categories: [] });
      },
    );
    return () => controller.abort();
  }, [tenantId, attempt]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  const current = state.tenantId === tenantId
    ? state
    : { tenantId, status: 'loading' as const, categories: [] };
  return { ...current, retry };
}
